import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import HeroCarousel from './HeroCarousel';
import CarouselControls from './CarouselControls';
import Loader from '../Loader/Loader';
import { logHeroCardViewed, logHeroCtaClicked, logHeroSwipe } from '../../shared/repos/marketing/braze';
import { filterContentCards } from '../../shared/utilities/braze';
import { routeCountryPath } from '../../shared/utilities/common';
import amplitude from '../../shared/utilities/amplitude';
import { resolveAmplitudePageName } from '../../shared/utilities/amplitudePageMapping';
import {
  HERO_PROMO_TEMPLATE,
  HERO_INTRO_PROMO_TEMPLATE,
  HERO_PROMO_IMAGE_TEMPLATE,
  HERO_PROMO_TEXT_TEMPLATE
} from '../../shared/constants/contentCards';

// Hero banner templates used for filtering
const HERO_BANNER_TEMPLATES = [
  HERO_PROMO_TEMPLATE,
  HERO_INTRO_PROMO_TEMPLATE,
  HERO_PROMO_IMAGE_TEMPLATE,
  HERO_PROMO_TEXT_TEMPLATE
];

const NAV_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

/**
 * FullScreenHeroCarousel - Full viewport height hero carousel
 * Sits below fixed header, uses remaining viewport height
 */
const FullScreenHeroCarousel = ({
                                  contentCards,
                                  defaultHeroBanner,
                                  selectedStore,
                                  onCtaClick,
                                  openDeliveryWidget,
                                  history
                                }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filter content cards using the same logic as original carousel
  // Note: filterContentCards expects the contentCards object with a .cards property, not the array
  const cards = useMemo(() => {
    // Don't filter if contentCards is still loading
    if (contentCards === null) {
      return [];
    }

    const storeId = selectedStore?.id ? parseInt(selectedStore.id, 10) : null;

    const filteredCards = filterContentCards(
      contentCards,
      HERO_BANNER_TEMPLATES,
      storeId,
      typeof window !== 'undefined' ? window.location.href : null
    );

    // Map fullScreen* video and image keys to standard keys for HeroSlide compatibility
    const mappedCards = filteredCards.map(card => {
      if (!card.extras) {
        return card;
      }

      const {
        fullScreenDesktopVideoUrl,
        fullScreenMobileVideoUrl,
        fullScreenDesktopImageUrl,
        fullScreenMobileImageUrl,
        ...restExtras
      } = card.extras;

      return {
        ...card,
        extras: {
          ...restExtras,
          ...(fullScreenDesktopVideoUrl && {
            desktopVideoUrl: fullScreenDesktopVideoUrl
          }),
          ...(fullScreenMobileVideoUrl && {
            mobileVideoUrl: fullScreenMobileVideoUrl
          }),
          ...(fullScreenDesktopImageUrl && {
            desktopImageUrl: fullScreenDesktopImageUrl
          }),
          ...(fullScreenMobileImageUrl && {
            mobileImageUrl: fullScreenMobileImageUrl
          })
        }
      };
    });

    // If no filtered cards, use default banner
    if (mappedCards.length === 0 && defaultHeroBanner) {
      return [defaultHeroBanner];
    }

    return mappedCards;
  }, [contentCards, defaultHeroBanner, selectedStore]);

  const isLoading = contentCards === null;

  // Keep currentIndex valid if the filtered card list changes.
  useEffect(() => {
    setCurrentIndex(prevIndex => {
      if (cards.length === 0) {
        return 0;
      }

      return Math.min(prevIndex, cards.length - 1);
    });
  }, [cards.length]);

  // Track card view on index change
  useEffect(() => {
    if (cards.length > 0 && cards[currentIndex]) {
      const card = cards[currentIndex];

      logHeroCardViewed(
        card,
        currentIndex,
        card.extras?.campaign,
        card.extras?.region
      );
    }
  }, [currentIndex, cards]);

  // handles all wheel, keyboard, dots, and loop sync.
  const handleSlideChange = useCallback((newIndex, direction = 'next') => {
    if (cards.length <= 1) {
      return;
    }

    if (newIndex < 0 || newIndex >= cards.length) {
      return;
    }

    if (newIndex === currentIndex) {
      return;
    }

    logHeroSwipe(direction, currentIndex, newIndex);
    setCurrentIndex(newIndex);
  }, [cards.length, currentIndex]);

  // Handle click
  const handleClick = useCallback((card, destination) => {
    logHeroCtaClicked(card.id, card.linkText || 'View', destination);

    const pageName = resolveAmplitudePageName(history?.location?.pathname) || 'Home';

    amplitude.trackClickedPromotionElement({
      pageName,
      cardSort: card.extras?.sort,
    });

    amplitude.trackClickPageContent({
      pageName,
      contentSection: 'Hero Content Card',
    });

    if (!destination || destination === '#') {
      return;
    }

    const isExternal = destination.startsWith('http://') || destination.startsWith('https://');
    const isHashOnly = destination.startsWith('#');

    // Hash-only link, e.g. #ice-cream: redirect to menu with hash
    if (isHashOnly) {
      const hash = destination.slice(1);

      if (openDeliveryWidget) {
        openDeliveryWidget(hash);
      } else if (history) {
        history.push(routeCountryPath(`/menu#${hash}`));
      }

      return;
    }

    // On homepage with internal link containing hash: open delivery widget
    if (
      history?.location?.pathname === '/'
      && !isExternal
      && destination.includes('#')
    ) {
      const hash = destination.split('#')[1];

      if (openDeliveryWidget) {
        openDeliveryWidget(hash);
      } else if (history) {
        history.push(routeCountryPath(`/menu#${hash}`));
      }

      return;
    }

    if (isExternal) {
      window.open(destination, '_blank', 'noopener,noreferrer');
    } else if (destination && history) {
      history.push(routeCountryPath(
        destination.startsWith('/') ? destination : `/${destination}`
      ));
    }

    if (onCtaClick) {
      onCtaClick(card, destination);
    }
  }, [history, onCtaClick, openDeliveryWidget]);

  // Handle dot click
  const handleDotClick = useCallback((index) => {
    if (index === currentIndex) {
      return;
    }

    const direction = index > currentIndex ? 'next' : 'prev';
    handleSlideChange(index, direction);
  }, [currentIndex, handleSlideChange]);

  /**
   * Handle non-adjacent keyboard commands from HeroCarousel.
   *
   * ArrowUp and ArrowDown are handled by HeroCarousel itself so the animation
   * direction stays synchronized with the infinite-loop transition.
   */
  const handleKeyDown = useCallback((key, e) => {
    const eventKey = key || e?.key;

    if (e && NAV_KEYS.includes(eventKey)) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (cards.length <= 1) {
      return;
    }

    if (eventKey === 'Home') {
      handleSlideChange(0, 'prev');
    } else if (eventKey === 'End') {
      handleSlideChange(cards.length - 1, 'next');
    }
  }, [cards.length, handleSlideChange]);

  useEffect(() => {
    return () => {
    };
  }, []);

  // Full height style for all states
  const fullHeightStyle = {
    height: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a1a',
    position: 'relative',
    zIndex: 1,
    touchAction: 'none'
  };

  if (isLoading) {
    return (
      <div style={fullHeightStyle}>
        <Loader />
      </div>
    );
  }

  if (!cards.length) {
    return (
      <div style={fullHeightStyle}>
        <div className="text-white text-center p-8">
          <p className="text-xl">Loading featured content...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={fullHeightStyle}>
      <HeroCarousel
        cards={cards}
        currentIndex={currentIndex}
        onKeyDown={handleKeyDown}
        onCtaClick={handleClick}
        onIndexChange={handleSlideChange}
      />

      <CarouselControls
        total={cards.length}
        current={currentIndex}
        onDotClick={handleDotClick}
      />
    </div>
  );
};

FullScreenHeroCarousel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  contentCards: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  defaultHeroBanner: PropTypes.object,
  selectedStore: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  onCtaClick: PropTypes.func,
  openDeliveryWidget: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string
    })
  })
};

FullScreenHeroCarousel.defaultProps = {
  contentCards: null,
  defaultHeroBanner: null,
  selectedStore: { id: null },
  onCtaClick: null,
  openDeliveryWidget: null,
  history: null
};

export const mapStateToProps = state => {
  const { contentCards, defaultHeroBanner } = state.braze;
  const { selectedStore } = state.user;

  return {
    contentCards,
    defaultHeroBanner,
    selectedStore
  };
};

export const FullScreenHeroCarouselUnconnected = FullScreenHeroCarousel;

export default connect(mapStateToProps)(withRouter(FullScreenHeroCarousel));
